import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
  useRef,
} from "react";
import { callErrorToast } from "../../../utilities/utilities";
import { ErrorResponse } from "../../../Models/Error";
import { KpiService } from "../../../services/shipment/kpi.ts";
import {
  KpiShipmentPerformanceFilter,
  KpiShipmentPerformance,
} from "../../../Models/KpiShipmentPerformance";

const kpiService = new KpiService();

interface ShipmentsPerformancesProviderProps {
  children: ReactNode;
  filters: KpiShipmentPerformanceFilter;
}

interface ShipmentsPerformancesContextValue {
  data: KpiShipmentPerformance[];
  loader: boolean;
  filters: KpiShipmentPerformanceFilter;
}

const ShipmentsPerformancesContext = createContext<
  ShipmentsPerformancesContextValue | undefined
>(undefined);

const ShipmentsPerformancesProvider: React.FC<
  ShipmentsPerformancesProviderProps
> = ({ children, filters }) => {
  const [data, setData] = useState<KpiShipmentPerformance[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [error, setError] =
    useState<ErrorResponse<KpiShipmentPerformance> | null>(null);

  const prevFilters = useRef<KpiShipmentPerformanceFilter>(filters);

  const getPerformances = (): void => {
    setLoader(true);
    kpiService
      .getPerformances(filters)
      .then((res) => {
        setLoader(false);
        setData(res?.data);
      })
      .catch((err) => {
        setLoader(false);
        setError(err);
      });

    return;
  };

  useEffect(() => {
    if (error) {
      callErrorToast(error);
    }
  }, [error]);

  useEffect(() => {
    if (
      filters &&
      JSON.stringify(prevFilters.current) !== JSON.stringify(filters)
    ) {
      getPerformances();
      prevFilters.current = filters;
    }
  }, [filters]);

  useEffect(() => {
    getPerformances();
  }, []);

  return (
    <ShipmentsPerformancesContext.Provider
      value={{
        data,
        loader,
        filters,
      }}
    >
      {children}
    </ShipmentsPerformancesContext.Provider>
  );
};

/**
 * Custom hook to access the ShipmentsPerformancesContext.
 * Ensures that the context is used only within a ShipmentsPerformancesProvider.
 */
const useShipmentsPerformancesContext =
  (): ShipmentsPerformancesContextValue => {
    const context = useContext(ShipmentsPerformancesContext);
    if (!context) {
      throw new Error(
        "useShipmentsPerformancesContext must be used within a ShipmentsPerformancesProvider"
      );
    }
    return context;
  };

// Export the provider and custom hook for use in other components
export { ShipmentsPerformancesProvider, useShipmentsPerformancesContext };
